import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { MoreVertical, Flag, Trash2, Loader2, X, Heart, Eye, Share2, ChevronDown, ChevronLeft, ChevronRight, List } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { DailyEntriesModal } from "./DailyEntriesModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInDays } from "date-fns";
import { useState, useEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MiniGraphOverlay } from "./MiniGraphOverlay";
import { getLocalizedMetricName } from "@/lib/utils";
import { FEATURED_SNAP_ID, FEATURED_SNAP_IMAGE_URL, FEATURED_SNAP_OVERLAY_URL } from "@/lib/constants";

interface ProgressSnapViewerProps {
  snap: any | null;
  onClose: () => void;
  currentUserId?: string;
  metricEmoji?: string;
  metricLabel?: string;
  onDelete?: () => void;
  hasNavbar?: boolean;
  sameDateSnaps?: any[]; // Array of all snaps from the same date for swipe navigation
}

export const ProgressSnapViewer = ({

  snap,
  onClose,
  currentUserId,
  metricEmoji,
  metricLabel,
  onDelete,
  hasNavbar = false,
  sameDateSnaps = [],
}: ProgressSnapViewerProps) => {
  const { t } = useTranslation();
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [isTextScrollable, setIsTextScrollable] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(true);
  const imageLoadedRef = useState(new Map<string, boolean>())[0];
  const [suggestedSnaps, setSuggestedSnaps] = useState<any[]>([]);
  const [currentSuggestedIndex, setCurrentSuggestedIndex] = useState(-1);
  const [isLoadingSuggested, setIsLoadingSuggested] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchCurrentY, setTouchCurrentY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [nextSnapPreview, setNextSnapPreview] = useState<any>(null);
  const [prevSnapPreview, setPrevSnapPreview] = useState<any>(null);
  const [releaseOffset, setReleaseOffset] = useState<number>(0);
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null);
  const [justTransitioned, setJustTransitioned] = useState(false);
  const [hasShownSwipeIndicator, setHasShownSwipeIndicator] = useState(false);
  const hasFetchedRef = useRef(false);
  const initialSnapIdRef = useRef<string | null>(null);
  const [dailyEntriesModalOpen, setDailyEntriesModalOpen] = useState(false);
  const [featuredBlurred, setFeaturedBlurred] = useState(false);

  // Horizontal swipe state for before/after metric comparison
  const [metricSnaps, setMetricSnaps] = useState<any[]>([]);
  const [hPageIndex, setHPageIndex] = useState(0);
  const [hInitialPage, setHInitialPage] = useState(0);
  // Custom split indices for non-adjacent comparison
  const [splitLeftCustomIdx, setSplitLeftCustomIdx] = useState<number | null>(null);
  const [splitRightCustomIdx, setSplitRightCustomIdx] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchCurrentX, setTouchCurrentX] = useState<number | null>(null);
  const [swipeAxis, setSwipeAxis] = useState<'none' | 'horizontal' | 'vertical'>('none');
  const [hTransitionTarget, setHTransitionTarget] = useState<number | null>(null);

  const snapDate = snap?.snap_date ? new Date(snap.snap_date + 'T00:00:00') : new Date();
  const isOwnSnap = currentUserId === snap?.user_id;
  // Use higher positioning when navbar is present (viewing from feed)
  const useHigherPosition = hasNavbar;
  const queryClient = useQueryClient();

  // Check if viewer can access the snap owner's profile data (for showing entries button)
  const { data: canViewData } = useQuery({
    queryKey: ["can-view-profile-data", snap?.user_id, currentUserId],
    queryFn: async () => {
      if (!currentUserId || !snap?.user_id || isOwnSnap) return true;
      const { data, error } = await supabase.rpc("can_view_profile_data", {
        profile_id: snap.user_id,
        viewer_id: currentUserId,
      });
      if (error) {
        console.error("Error checking profile access:", error);
        return false;
      }
      return data;
    },
    enabled: !!currentUserId && !!snap?.user_id && !isOwnSnap,
  });
  const canViewProfileData = isOwnSnap || (canViewData ?? false);
  const navigate = useNavigate();
  
  // Check if currently viewing a suggested snap
  const isViewingSuggestedSnap = currentSuggestedIndex >= 0 && suggestedSnaps.length > 0;
  
  // Get current snap to display (either the passed snap or suggested snap)
  const rawDisplaySnap = isViewingSuggestedSnap ? suggestedSnaps[currentSuggestedIndex] : snap;

  // If this is the featured snap, override its image_url with the promotional image
  const isFeaturedSnap = rawDisplaySnap?.id === FEATURED_SNAP_ID;
  const displaySnap = isFeaturedSnap
    ? { ...rawDisplaySnap, image_url: FEATURED_SNAP_IMAGE_URL, text_content: "Take progress pics on your Track page!" }
    : rawDisplaySnap;
  
  // Featured snap: blur image after 4 seconds (only once image has loaded) and show promo overlay
  const featuredImageLoadedRef = useRef(false);
  const featuredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isFeaturedSnap) {
      setFeaturedBlurred(false);
      featuredImageLoadedRef.current = false;
      if (featuredTimerRef.current) clearTimeout(featuredTimerRef.current);
      return;
    }
    setFeaturedBlurred(false);
    featuredImageLoadedRef.current = false;
    return () => {
      if (featuredTimerRef.current) clearTimeout(featuredTimerRef.current);
    };
  }, [isFeaturedSnap, displaySnap?.id]);

  // Inject keyframe animation for featured snap overlay
  useEffect(() => {
    const styleId = 'featured-overlay-keyframes';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes featuredOverlayFadeIn {
        from { opacity: 0; transform: scale(0.92); }
        to   { opacity: 1; transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Reset swipe indicator state when opening a new snap viewer session
  useEffect(() => {
    if (snap?.id) {
      // If this is a different snap than we had before, reset everything
      if (initialSnapIdRef.current !== snap.id) {
        console.log('[SwipeIndicator] New snap session detected, resetting indicator state');
        setHasShownSwipeIndicator(false);
        initialSnapIdRef.current = snap.id;
      }
    }
  }, [snap?.id]);
  
  // Hide indicator once user navigates away from initial snap
  useEffect(() => {
    if (displaySnap?.id && initialSnapIdRef.current && displaySnap.id !== initialSnapIdRef.current) {
      setHasShownSwipeIndicator(true);
    }
  }, [displaySnap?.id]);
  
  // Reset image loading state only when snap ID actually changes
  useEffect(() => {
    if (displaySnap?.id) {
      // Check if we've already loaded this image
      if (imageLoadedRef.has(displaySnap.id)) {
        console.log("Image already loaded for snap:", displaySnap.id);
        setImageLoading(false);
      } else if (displaySnap?.image_url || displaySnap?.video_url) {
        console.log("New snap with media, setting loading to true:", displaySnap.id);
        setImageLoading(true);
      } else {
        console.log("Text-only snap, no loading needed");
        setImageLoading(false);
      }
    }
  }, [displaySnap?.id, imageLoadedRef]);
  
  // Log whenever imageLoading changes
  useEffect(() => {
    console.log("imageLoading state is now:", imageLoading);
  }, [imageLoading]);

  // Preload next snap media when available
  useEffect(() => {
    if (isViewingSuggestedSnap && currentSuggestedIndex < suggestedSnaps.length - 1) {
      const nextSnap = suggestedSnaps[currentSuggestedIndex + 1];
      if (nextSnap?.video_url) {
        // Preload the video
        const vid = document.createElement('video');
        vid.preload = 'auto';
        vid.src = nextSnap.video_url;
        console.log("Preloading next snap video:", nextSnap.id);
      } else if (nextSnap?.image_url) {
        // Preload the image
        const img = new Image();
        img.src = nextSnap.image_url;
        console.log("Preloading next snap image:", nextSnap.id);
      }
    }
  }, [isViewingSuggestedSnap, currentSuggestedIndex, suggestedSnaps]);

  // Pre-fetch suggested snaps immediately when viewer opens (if viewing someone else's snap)
  useEffect(() => {
    if (snap && !isOwnSnap && currentUserId && suggestedSnaps.length === 0 && !isLoadingSuggested && !hasFetchedRef.current) {
      // Fetch immediately so snaps are ready for quick swipe
      hasFetchedRef.current = true;
      fetchSuggestedSnaps();
    }
  }, [snap?.id, isOwnSnap, currentUserId]);
  
  // Initialize with same-date snaps if provided (for swipe navigation through snaps from same day)
  useEffect(() => {
    if (sameDateSnaps && sameDateSnaps.length > 1) {
      // Find the index of the current snap in the array
      const currentIndex = sameDateSnaps.findIndex(s => s.id === snap?.id);
      console.log('Snap IDs in array:', sameDateSnaps.map(s => s.id));
      console.log('Current snap ID:', snap?.id);
      console.log('Found at index:', currentIndex);
      
      if (currentIndex >= 0) {
        console.log('Initializing swipe with', sameDateSnaps.length, 'snaps, starting at index', currentIndex);
        setSuggestedSnaps(sameDateSnaps);
        setCurrentSuggestedIndex(currentIndex);
      } else {
        // Snap not found in array, default to starting at 0
        console.log('Snap not found in array, starting at index 0');
        setSuggestedSnaps(sameDateSnaps);
        setCurrentSuggestedIndex(0);
      }
    }
    // Note: Don't reset suggested snaps here - they may have been loaded via swipe discover mode
    // Only initialize if sameDateSnaps is explicitly provided with multiple snaps
  }, [sameDateSnaps, snap?.id]);
  
  // Detect if on desktop (width > 768px)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle sharing snap
  const handleShare = async () => {
    if (!displaySnap) return;
    
    const shareUrl = `${window.location.origin}/snap/${displaySnap.id}`;
    const shareText = `Check out this progress snap from ${userProfile?.username || 'a user'}!`;
    
    // Share via native share if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Progress Snap',
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled share
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('notifications.toast.link_copied_to_clipboard'));
      } catch (error) {
        toast.error(t('notifications.toast.failed_to_copy_link'));
      }
    }


  };

  // Handle clicking on profile picture
  const handleProfileClick = () => {
    if (userProfile?.username) {
      navigate(`/${userProfile.username}`);
    }
  };

  // Fetch suggested snaps
  const fetchSuggestedSnaps = async (showToastIfEmpty: boolean = false) => {
    if (!currentUserId || isLoadingSuggested) return;
    
    setIsLoadingSuggested(true);
    try {
      const { data, error } = await (supabase as any).rpc("get_suggested_progress_snaps", {
        p_user_id: currentUserId,
        p_limit: 20,
        p_exclude_snap_id: snap?.id || null,
      });

      if (error) {
        console.error("Failed to fetch suggested snaps:", error);
        if (showToastIfEmpty) {
          toast.error(t('notifications.toast.failed_to_load_suggested_snaps'));
        }
        return;
      }

      if (!data || data.length === 0) {
        // Only show toast if user actively tried to swipe (not on initial pre-fetch)
        if (showToastIfEmpty) {
          toast("No more snaps to discover right now", {
            description: "Check back later for more content!",
          });
        }
        return;
      }

      setSuggestedSnaps(data);
      // Don't set currentSuggestedIndex yet - let it be set by swipe or dice button
    } catch (error) {
      console.error("Error fetching suggested snaps:", error);
      if (showToastIfEmpty) {
        toast.error(t('notifications.toast.failed_to_load_suggested_snaps'));
      }
    } finally {
      setIsLoadingSuggested(false);
    }
  };

  // Fetch all snaps for the same user+metric for horizontal before/after comparison
  useEffect(() => {
    let cancelled = false;
    
    setMetricSnaps([]);
    setHPageIndex(0);
    setHInitialPage(0);
    setSwipeAxis('none');
    setTouchStartX(null);
    setTouchCurrentX(null);
    setHTransitionTarget(null);
    
    if (!displaySnap?.user_id || !displaySnap?.metric_name ||
        (!displaySnap?.image_url && !displaySnap?.video_url)) return;
    
    const fetchSnaps = async () => {
      try {
        const { data, error } = await (supabase as any)
          .from("progress_snaps")
          .select("*")
          .eq("user_id", displaySnap.user_id)
          .eq("metric_name", displaySnap.metric_name)
          .or("image_url.not.is.null,video_url.not.is.null")
          .order("snap_date", { ascending: true });
        
        if (cancelled || error || !data || data.length <= 1) return;
        
        setMetricSnaps(data);
        const currentIdx = data.findIndex((s: any) => s.id === displaySnap.id);
        if (currentIdx >= 0) {
          const initialPage = currentIdx * 2;
          setHPageIndex(initialPage);
          setHInitialPage(initialPage);
        }
      } catch (err) {
        if (!cancelled) console.error("Error fetching metric snaps:", err);
      }
    };
    
    fetchSnaps();
    return () => { cancelled = true; };
  }, [displaySnap?.id]);

  // Reset custom split indices when page changes
  useEffect(() => {
    setSplitLeftCustomIdx(null);
    setSplitRightCustomIdx(null);
  }, [hPageIndex]);

  // Preload metric snap images for smooth horizontal transitions
  useEffect(() => {
    metricSnaps.forEach((s: any) => {
      if (s.image_url) { const img = new Image(); img.src = s.image_url; }
    });
  }, [metricSnaps]);

  // Navigate to next suggested snap with animation
  const handleNextSuggestedSnap = () => {
    if (currentSuggestedIndex >= suggestedSnaps.length - 1) {
      // Reached the end - fetch more
      toast.success("That's all! Finding more snaps...");
      fetchSuggestedSnaps();
      return;
    }

    // Preview the next snap before transitioning
    setNextSnapPreview(suggestedSnaps[currentSuggestedIndex + 1]);

    // Get screen height for full swipe animation
    const screenHeight = window.innerHeight;
    setReleaseOffset(0);

    // Animate transition (for dice button - simulate full swipe)
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSuggestedIndex(currentSuggestedIndex + 1);
      setIsTransitioning(false);
      setNextSnapPreview(null);
      setReleaseOffset(0);
    }, 300);
  };

  // Touch gesture handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isDesktop || isTransitioning) return;
    
    setTouchStartY(e.touches[0].clientY);
    setTouchCurrentY(e.touches[0].clientY);
    setTouchStartX(e.touches[0].clientX);
    setTouchCurrentX(e.touches[0].clientX);
    setTouchStartTime(Date.now());
    setSwipeAxis('none');
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY === null || touchStartX === null || isDesktop || isTransitioning) return;
    
    const currentY = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const deltaY = touchStartY - currentY;
    const deltaX = touchStartX - currentX;
    
    setTouchCurrentY(currentY);
    setTouchCurrentX(currentX);
    
    // Determine swipe axis if not yet locked
    if (swipeAxis === 'none') {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX > 12 || absY > 12) {
        if (absX > absY && metricSnaps.length > 1) {
          const maxPage = (metricSnaps.length - 1) * 2;
          if ((deltaX > 0 && hPageIndex < maxPage) || (deltaX < 0 && hPageIndex > 0)) {
            setSwipeAxis('horizontal');
            return;
          }
        }
        setSwipeAxis('vertical');
      } else {
        return;
      }
    }
    
    if (swipeAxis === 'horizontal') return;
    
    // Vertical swipe logic
    const hasSameDateSnaps = sameDateSnaps && sameDateSnaps.length > 1;
    if (isOwnSnap && !hasSameDateSnaps) return;
    
    // Swiping UP (positive deltaY)
    if (deltaY > 20) {
      setIsDragging(true);
      setPrevSnapPreview(null);
      
      if (!isViewingSuggestedSnap && suggestedSnaps.length === 0 && !isLoadingSuggested) {
        fetchSuggestedSnaps();
      }
      
      if (!nextSnapPreview) {
        if (isViewingSuggestedSnap && currentSuggestedIndex < suggestedSnaps.length - 1) {
          setNextSnapPreview(suggestedSnaps[currentSuggestedIndex + 1]);
        } else if (!isViewingSuggestedSnap && suggestedSnaps.length > 0) {
          setNextSnapPreview(suggestedSnaps[0]);
        }
      }
    } 
    else if (deltaY < -20) {
      setIsDragging(true);
      setNextSnapPreview(null);
      
      if (!prevSnapPreview && isViewingSuggestedSnap) {
        if (currentSuggestedIndex > 0) {
          setPrevSnapPreview(suggestedSnaps[currentSuggestedIndex - 1]);
        } else if (currentSuggestedIndex === 0) {
          setPrevSnapPreview(snap);
        }
      }
    } 
    else if (Math.abs(deltaY) < 10) {
      setIsDragging(false);
      setNextSnapPreview(null);
      setPrevSnapPreview(null);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartY === null || touchCurrentY === null || isDesktop) return;
    
    // Handle horizontal swipe completion
    if (swipeAxis === 'horizontal' && touchStartX !== null && touchCurrentX !== null) {
      const deltaX = touchStartX - touchCurrentX;
      const deltaTime = touchStartTime ? Date.now() - touchStartTime : 0;
      const velocity = deltaTime > 0 ? Math.abs(deltaX) / deltaTime : 0;
      
      const screenWidth = window.innerWidth;
      const swipeThreshold = screenWidth * 0.2;
      const maxPage = (metricSnaps.length - 1) * 2;
      const isSwipeLeft = (velocity > 0.3 && deltaX > 30) || deltaX > swipeThreshold;
      const isSwipeRight = (velocity > 0.3 && deltaX < -30) || deltaX < -swipeThreshold;
      
      if (isSwipeLeft && hPageIndex < maxPage) {
        setHTransitionTarget(1);
        setTimeout(() => {
          setHPageIndex(prev => Math.min(prev + 1, maxPage));
          setHTransitionTarget(null);
        }, 310);
      } else if (isSwipeRight && hPageIndex > 0) {
        setHTransitionTarget(-1);
        setTimeout(() => {
          setHPageIndex(prev => Math.max(prev - 1, 0));
          setHTransitionTarget(null);
        }, 310);
      }
      
      setSwipeAxis('none');
      setTouchStartX(null);
      setTouchCurrentX(null);
      setTouchStartY(null);
      setTouchCurrentY(null);
      setTouchStartTime(null);
      setIsDragging(false);
      return;
    }
    
    // Reset horizontal state
    setSwipeAxis('none');
    setTouchStartX(null);
    setTouchCurrentX(null);
    
    const deltaY = touchStartY - touchCurrentY;
    const deltaTime = touchStartTime ? Date.now() - touchStartTime : 0;
    const velocity = deltaTime > 0 ? Math.abs(deltaY) / deltaTime : 0; // pixels per millisecond
    
    // Get screen height
    const screenHeight = window.innerHeight;
    const swipeThreshold = screenHeight * 0.5; // 50% of screen height - seam must reach midpoint
    const quickSwipeVelocity = 0.3; // pixels per ms - if faster than this, count as quick swipe (lowered for better first-snap feel)
    const minSwipeDistance = 40; // minimum distance for quick swipe to count (lowered for better responsiveness)
    
    // Check if it's a quick swipe (fast velocity with reasonable distance) OR reached midpoint
    const isQuickSwipeUp = velocity > quickSwipeVelocity && deltaY > minSwipeDistance;
    const isQuickSwipeDown = velocity > quickSwipeVelocity && deltaY < -minSwipeDistance;
    const reachedMidpointUp = deltaY > swipeThreshold;
    const reachedMidpointDown = deltaY < -swipeThreshold;
    
    // SWIPE UP - go to next snap
    const hasSameDateSnaps = sameDateSnaps && sameDateSnaps.length > 1;
    if ((isQuickSwipeUp || reachedMidpointUp) && (!isOwnSnap || hasSameDateSnaps) && !isTransitioning) {
      if (isViewingSuggestedSnap) {
        // Check if we're at the last snap
        if (currentSuggestedIndex >= suggestedSnaps.length - 1) {
          // At the end - show toast and close (or just reset for same-date snaps)
          if (hasSameDateSnaps) {
            setNextSnapPreview(null);
            return;
          }
          toast("That's all the snaps we could find!", {
            description: "Thanks for exploring!",
          });
          setNextSnapPreview(null);
          return;
        }
        
        // Already in discover mode - go to next snap
        // Store the current drag offset to continue animation smoothly from this position
        setReleaseOffset(deltaY);
        setIsTransitioning(true);
        
        // Complete the transition
        setTimeout(() => {
          setCurrentSuggestedIndex(currentSuggestedIndex + 1);
          setIsTransitioning(false);
          setNextSnapPreview(null);
          setReleaseOffset(0);
          setJustTransitioned(true);
          // Reset flag after animation complete
          setTimeout(() => setJustTransitioned(false), 50);
        }, 400);
      } else if (suggestedSnaps.length > 0) {
        // Just loaded snaps - transition to first suggested snap
        setReleaseOffset(deltaY);
        setIsTransitioning(true);
        
        setTimeout(() => {
          setCurrentSuggestedIndex(0);
          setIsTransitioning(false);
          setNextSnapPreview(null);
          setReleaseOffset(0);
          setJustTransitioned(true);
          // Reset flag after animation complete
          setTimeout(() => setJustTransitioned(false), 50);
        }, 400);
      } else {
        // Snaps not ready yet - fetch if needed
        if (!isLoadingSuggested && suggestedSnaps.length === 0) {
          fetchSuggestedSnaps(true); // Show toast since user actively swiped
        }
      }
    } 
    // SWIPE DOWN - go to previous snap
    else if ((isQuickSwipeDown || reachedMidpointDown) && (!isOwnSnap || hasSameDateSnaps) && !isTransitioning && isViewingSuggestedSnap) {
      if (currentSuggestedIndex === 0) {
        // At first snap - for same-date snaps, there's no "original" snap to go back to
        // Just reset the previews and stay at index 0
        if (hasSameDateSnaps) {
          setPrevSnapPreview(null);
          return;
        }
        
        // For discover mode - go back to original snap (keep snaps cached)
        setReleaseOffset(Math.abs(deltaY));
        setIsTransitioning(true);
        
        setTimeout(() => {
          setCurrentSuggestedIndex(-1);
          // Don't clear snaps - keep them cached for next swipe up
          setIsTransitioning(false);
          setPrevSnapPreview(null);
          setReleaseOffset(0);
          setJustTransitioned(true);
          setTimeout(() => setJustTransitioned(false), 50);
        }, 400);
      } else {
        // Go to previous suggested snap
        setReleaseOffset(Math.abs(deltaY));
        setIsTransitioning(true);
        
        setTimeout(() => {
          setCurrentSuggestedIndex(currentSuggestedIndex - 1);
          setIsTransitioning(false);
          setPrevSnapPreview(null);
          setReleaseOffset(0);
          setJustTransitioned(true);
          setTimeout(() => setJustTransitioned(false), 50);
        }, 400);
      }
    } else {
      // Reset preview if didn't complete swipe
      setNextSnapPreview(null);
      setPrevSnapPreview(null);
    }
    
    setTouchStartY(null);
    setTouchCurrentY(null);
    setTouchStartTime(null);
    setIsDragging(false);
  };

  // Exit discover mode
  const handleExitDiscover = () => {
    setSuggestedSnaps([]);
    setCurrentSuggestedIndex(-1);
    onClose();
  };

  // Fetch user profile for avatar
  // Use a distinct query key ("snap-user-profile") to avoid overwriting the full profile cache
  // used by useProfile (queryKey: ["profile", userId]) which includes is_premium and other fields.
  const { data: userProfile } = useQuery({
    queryKey: ["snap-user-profile", displaySnap?.user_id],
    queryFn: async () => {
      if (!displaySnap?.user_id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("username, avatar_url")
        .eq("id", displaySnap.user_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!displaySnap?.user_id,
  });

  // Fetch custom metric details if this is a custom metric snap
  const { data: customMetricInfo } = useQuery({
    queryKey: ["custom-metric", displaySnap?.user_id, displaySnap?.metric_name],
    queryFn: async () => {
      if (!displaySnap?.user_id || !displaySnap?.metric_name) return null;
      if (!displaySnap.metric_name.startsWith('custom_')) return null;
      
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("metric_name, metric_emoji")
        .eq("user_id", displaySnap.user_id)
        .eq("metric_key", displaySnap.metric_name)
        .maybeSingle();
      
      if (error) {
        console.error("Failed to fetch custom metric:", error);
        return null;
      }
      return data;
    },
    enabled: !!displaySnap?.user_id && !!displaySnap?.metric_name && displaySnap.metric_name.startsWith('custom_'),
  });

  // Fetch built-in metric definition if this is not a custom metric
  const { data: builtInMetricInfo } = useQuery({
    queryKey: ["built-in-metric", displaySnap?.metric_name],
    queryFn: async () => {
      if (!displaySnap?.metric_name || displaySnap.metric_name.startsWith('custom_')) return null;
      
      const { data, error } = await supabase
        .rpc('get_metric_definitions');
      
      if (error) {
        console.error("Failed to fetch metric definitions:", error);
        return null;
      }
      
      const metricDef = data?.find((m: any) => m.metric_key === displaySnap.metric_name);
      if (!metricDef) return null;
      
      return {
        metric_name: metricDef.label,
        metric_emoji: metricDef.emoji
      };
    },
    enabled: !!displaySnap?.metric_name && !displaySnap.metric_name.startsWith('custom_'),
  });

  // Use custom metric info, built-in metric info, or fall back to props
  const displayEmoji = customMetricInfo?.metric_emoji || builtInMetricInfo?.metric_emoji || metricEmoji;
  
  // For built-in metrics, use translated label; otherwise use the provided names
  const getDisplayLabel = () => {
    // Custom metric - use the custom metric name
    if (customMetricInfo?.metric_name) return customMetricInfo.metric_name;
    
    // Built-in metric - use translated name
    if (builtInMetricInfo?.metric_name && displaySnap?.metric_name) {
      return getLocalizedMetricName(displaySnap.metric_name, t, builtInMetricInfo.metric_name);
    }
    
    // Fallback to props or raw metric_name
    if (metricLabel) return metricLabel;
    if (displaySnap?.metric_name) {
      return getLocalizedMetricName(displaySnap.metric_name, t, displaySnap.metric_name);
    }
    return '';
  };
  const displayLabel = getDisplayLabel();

  // Fetch like count for this snap
  const { data: likeCount = 0 } = useQuery({
    queryKey: ["snap-likes-count", displaySnap?.id],
    queryFn: async () => {
      if (!displaySnap?.id) return 0;
      const { count, error } = await (supabase as any)
        .from("snap_likes")
        .select("*", { count: "exact", head: true })
        .eq("snap_id", displaySnap.id);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!displaySnap?.id,
  });

  // Check if current user has liked this snap
  const { data: userHasLiked = false } = useQuery({
    queryKey: ["snap-user-liked", displaySnap?.id, currentUserId],
    queryFn: async () => {
      if (!displaySnap?.id || !currentUserId) return false;
      const { data, error } = await (supabase as any)
        .from("snap_likes")
        .select("id")
        .eq("snap_id", displaySnap.id)
        .eq("user_id", currentUserId)
        .maybeSingle();
      
      if (error) throw error;
      return !!data;
    },
    enabled: !!displaySnap?.id && !!currentUserId && !isOwnSnap,
  });

  // Like/unlike mutation
  const likeMutation = useMutation({
    mutationFn: async () => {
      if (!displaySnap?.id || !currentUserId) throw new Error("Missing data");

      if (userHasLiked) {
        // Unlike
        const { error } = await (supabase as any)
          .from("snap_likes")
          .delete()
          .eq("snap_id", displaySnap.id)
          .eq("user_id", currentUserId);
        
        if (error) throw error;
        return "unliked";
      } else {
        // Like
        const { error } = await (supabase as any)
          .from("snap_likes")
          .insert({
            snap_id: displaySnap.id,
            user_id: currentUserId,
          });
        
        if (error) throw error;

        // Send notification
        try {
          const response = await supabase.functions.invoke("send-snap-like-notification", {
            body: {
              snapId: displaySnap.id,
              likerId: currentUserId,
              snapOwnerId: displaySnap.user_id,
              metricName: displaySnap.metric_name,
              snapDate: displaySnap.snap_date,
            },
          });
          
          if (response.error) {
            console.error("Notification function error:", response.error);
          }
        } catch (notifError) {
          console.error("Failed to send notification:", notifError);
          // Don't fail the like if notification fails
        }

        return "liked";
      }
    },
    onSuccess: () => {
      // Invalidate queries to refresh counts
      queryClient.invalidateQueries({ queryKey: ["snap-likes-count", displaySnap?.id] });
      queryClient.invalidateQueries({ queryKey: ["snap-user-liked", displaySnap?.id, currentUserId] });
    },
    onError: (error) => {
      console.error("Failed to toggle like:", error);
      toast.error(t('notifications.toast.failed_to_update_like'));
    },
  });

  // Animate in when snap opens (but not when transitioning between snaps)
  useEffect(() => {
    if (displaySnap) {
      // Don't re-trigger animation if we just transitioned via swipe
      if (!justTransitioned && !isViewingSuggestedSnap) {
        // Small delay to trigger animation for initial open
        setTimeout(() => setIsVisible(true), 10);
      } else if (!isVisible) {
        // Make sure it's visible if it's the first suggested snap
        setIsVisible(true);
      }
      
      // Track view for other users' snaps
      if (!isOwnSnap && currentUserId) {
        // Call increment view count function
        (async () => {
          try {
            await (supabase as any).rpc("increment_snap_view_count", { p_snap_id: displaySnap.id });
            console.log("View counted");
          } catch (error) {
            console.error("Failed to count view:", error);
          }
        })();
      }
    } else {
      setIsVisible(false);
    }
  }, [displaySnap?.id, isOwnSnap, currentUserId, justTransitioned, isViewingSuggestedSnap, isVisible]);

  // Handle scroll to track if at bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 5;
    setIsScrolledToBottom(isAtBottom);
  };

  // Check initial scroll state
  useEffect(() => {
    if (displaySnap?.text_content && !displaySnap?.image_url && !displaySnap?.video_url) {
      // For non-image layout, check if scrollable
      const textElement = document.getElementById('snap-text-content');
      if (textElement) {
        const scrollable = textElement.scrollHeight > textElement.clientHeight;
        setIsTextScrollable(scrollable);
        setIsScrolledToBottom(textElement.scrollHeight <= textElement.clientHeight);
      }
    }
  }, [displaySnap?.id]);

  const handleReport = async () => {
    if (!displaySnap || !currentUserId) return;
    
    setIsReporting(true);
    try {
      // Use the same report-user Edge Function that's used for reporting profiles
      const { error } = await supabase.functions.invoke("report-user", {
        body: {
          reportedUserId: displaySnap.user_id,
          reason: "inappropriate_content",
          details: `Progress snap reported - Metric: ${displaySnap.metric_name}, Date: ${displaySnap.snap_date}`,
        },
      });

      if (error) throw error;

      toast.success(t('notifications.toast.report_submitted'));
      setShowReportDialog(false);
      
      // If viewing suggested snap, go to next one
      if (isViewingSuggestedSnap) {
        handleNextSuggestedSnap();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to submit report:", error);
      toast.error(t('notifications.toast.failed_to_submit_report'));
    } finally {
      setIsReporting(false);
    }
  };

  const handleDelete = async () => {
    if (!displaySnap || !isOwnSnap) return;
    
    setIsDeleting(true);
    try {
      // Delete the snap from database
      const { error: deleteError } = await (supabase as any)
        .from("progress_snaps")
        .delete()
        .eq("id", displaySnap.id);

      if (deleteError) throw deleteError;

      // Delete the image from storage if it exists
      if (displaySnap.image_url) {
        const urlParts = displaySnap.image_url.split("/");
        const filePath = urlParts.slice(-2).join("/"); // Get userId/filename
        await supabase.storage.from("progress-snaps").remove([filePath]);
      }

      // Delete the video from storage if it exists
      if (displaySnap.video_url) {
        const videoUrlParts = displaySnap.video_url.split("/");
        const videoFilePath = videoUrlParts.slice(-2).join("/");
        await supabase.storage.from("progress-snaps").remove([videoFilePath]);
      }

      toast.success(t('notifications.toast.snap_deleted'));
      setShowDeleteDialog(false);
      onClose();
      
      // Call onDelete callback to refresh data
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error("Failed to delete snap:", error);
      toast.error(t('notifications.toast.failed_to_delete_snap'));
    } finally {
      setIsDeleting(false);
    }
  };

  const isOpen = !!snap || isViewingSuggestedSnap;

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Horizontal comparison computed values
  const hasMetricSnaps = metricSnaps.length > 1;
  const maxHPage = hasMetricSnaps ? (metricSnaps.length - 1) * 2 : 0;
  const isHCenteredView = hPageIndex % 2 === 0;
  const isSplitView = !isHCenteredView && hasMetricSnaps;
  
  const hCenteredIndex = isHCenteredView ? hPageIndex / 2 : null;
  
  // Preview thumbnail snaps for centered view
  const hPrevSnap = hasMetricSnaps && isHCenteredView && hCenteredIndex !== null && hCenteredIndex > 0
    ? metricSnaps[hCenteredIndex - 1] : null;
  const hNextSnap = hasMetricSnaps && isHCenteredView && hCenteredIndex !== null && hCenteredIndex < metricSnaps.length - 1
    ? metricSnaps[hCenteredIndex + 1] : null;
  
  // Split view data
  const hSplitLeftIndex = isSplitView ? (hPageIndex - 1) / 2 : null;
  const hSplitRightIndex = isSplitView ? (hPageIndex - 1) / 2 + 1 : null;
  // Effective indices (custom overrides for non-adjacent comparison)
  const effectiveLeftIdx = isSplitView ? (splitLeftCustomIdx ?? hSplitLeftIndex) : hSplitLeftIndex;
  const effectiveRightIdx = isSplitView ? (splitRightCustomIdx ?? hSplitRightIndex) : hSplitRightIndex;
  const splitLeftSnap = effectiveLeftIdx !== null ? metricSnaps[effectiveLeftIdx] : null;
  const splitRightSnap = effectiveRightIdx !== null ? metricSnaps[effectiveRightIdx] : null;
  const splitDaysDelta = splitLeftSnap && splitRightSnap
    ? Math.abs(differenceInDays(
        new Date(splitRightSnap.snap_date + 'T00:00:00'),
        new Date(splitLeftSnap.snap_date + 'T00:00:00')
      ))
    : 0;

  // Ref callback that forces autoplay+loop on video elements (works around React muted attribute bug)
  const forceAutoPlay = (el: HTMLVideoElement | null) => {
    if (!el) return;
    el.muted = true;
    el.loop = true;
    el.playsInline = true;
    el.play().catch(() => {});
  };

  // Helper: render media for a snap
  const renderSnapMedia = (s: any, pos: string = 'center center') => {
    if (s?.video_url) {
      return (
        <video ref={forceAutoPlay} src={s.video_url} className="w-full h-full object-cover pointer-events-none"
          style={{ objectPosition: pos }} autoPlay loop muted playsInline />
      );
    }
    if (s?.image_url) {
      return (
        <img src={s.image_url} alt="" className="w-full h-full object-cover pointer-events-none"
          style={{ objectPosition: pos }} />
      );
    }
    return <div className="w-full h-full bg-black" />;
  };

  // Helper: render a horizontal page by index
  const renderHPage = (pageIdx: number) => {
    if (!hasMetricSnaps || pageIdx < 0 || pageIdx > maxHPage) {
      return <div className="w-full h-full bg-black" />;
    }
    const objPos = useHigherPosition ? 'center bottom' : 'center center';
    if (pageIdx % 2 === 0) {
      return renderSnapMedia(metricSnaps[pageIdx / 2], objPos);
    } else {
      // Use custom indices if this is the current page and overrides are set
      const isCurrentPage = pageIdx === hPageIndex;
      const leftIdx = isCurrentPage && splitLeftCustomIdx !== null ? splitLeftCustomIdx : (pageIdx - 1) / 2;
      const rightIdx = isCurrentPage && splitRightCustomIdx !== null ? splitRightCustomIdx : leftIdx + 1;
      const leftSnap = metricSnaps[leftIdx];
      const rightSnap = metricSnaps[rightIdx];
      return (
        <div className="flex w-full h-full">
          <div className="w-[calc(50%-0.5px)] h-full overflow-hidden relative flex-shrink-0">
            {renderSnapMedia(leftSnap, objPos)}
          </div>
          <div className="w-[1px] h-full bg-white/50 flex-shrink-0" />
          <div className="w-[calc(50%-0.5px)] h-full overflow-hidden relative flex-shrink-0">
            {renderSnapMedia(rightSnap, objPos)}
          </div>
        </div>
      );
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 z-50 transition-opacity duration-300"
        style={{ opacity: isVisible ? 1 : 0 }}
        onClick={onClose}
      />

      {/* Mobile Bottom Sheet / Desktop Centered */}
      <div 
        className="fixed z-[60] overflow-hidden"
        style={{ 
          maxHeight: isDesktop ? '100vh' : (useHigherPosition ? 'calc(100vh - env(safe-area-inset-top))' : '100vh'),
          bottom: isDesktop ? '0' : (useHigherPosition ? 'env(safe-area-inset-bottom, 0)' : '0'),
          left: isDesktop ? '50%' : '0',
          right: isDesktop ? 'auto' : '0',
          width: isDesktop ? 'min(430px, calc(100vh * 9 / 16))' : 'auto',
          transform: isDesktop ? 'translateX(-50%)' : 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Wrapper for vertical scroll animation */}
        <div 
          className="relative"
          style={{
            height: isDesktop ? '100vh' : (useHigherPosition ? 'calc(100vh - env(safe-area-inset-top))' : '100vh'),
          }}
        >
          {/* Current snap */}
          <div 
            className="absolute inset-0 bg-background overflow-hidden flex flex-col" 
            style={{ 
              transform: isTransitioning 
                ? (prevSnapPreview ? 'translateY(100%)' : 'translateY(-100%)')
                : isDragging && touchStartY !== null && touchCurrentY !== null
                  ? `translateY(${-(touchStartY - touchCurrentY)}px)`
                  : (isVisible || justTransitioned)
                    ? 'translateY(0)' 
                    : 'translateY(100%)',
              transition: (isDragging || justTransitioned) ? 'none' : 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1)',
              borderTopLeftRadius: isDesktop ? '1rem' : (useHigherPosition ? '1rem' : '0'),
              borderTopRightRadius: isDesktop ? '1rem' : (useHigherPosition ? '1rem' : '0'),
              borderBottomLeftRadius: isDesktop ? '1rem' : '0',
              borderBottomRightRadius: isDesktop ? '1rem' : '0',
            }}
          >
          {/* Header - only show for snaps without images */}
          {!displaySnap?.image_url && !displaySnap?.video_url && (
            <>
              <div className="flex items-center justify-between p-4 border-b shrink-0">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {displayEmoji && <span className="text-lg">{displayEmoji}</span>}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold truncate">{displayLabel}</h2>
                    <p className="text-sm text-muted-foreground">
                      {format(snapDate, "MMMM d, yyyy")}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 shrink-0">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8" 
                    onClick={isViewingSuggestedSnap ? handleExitDiscover : onClose}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Mini graph overlay for non-image snaps */}
              {displaySnap?.user_id && displaySnap?.metric_name && (
                <MiniGraphOverlay
                  userId={displaySnap.user_id}
                  metricKey={displaySnap.metric_name}
                  metricEmoji={displayEmoji}
                  metricLabel={displayLabel}
                  snapDate={snapDate}
                />
              )}
            </>
          )}
          
          {/* Header overlay for image snaps - fixed top */}
          {(displaySnap?.image_url || displaySnap?.video_url) && (
            <>
              {/* Title info - top left */}
              <div 
                className="absolute top-4 left-4 z-10 flex items-center gap-2"
              >
                <div className="bg-black/30 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-2">
                  {displayEmoji && <span className="text-lg">{displayEmoji}</span>}
                  <div>
                    <h2 className="text-sm font-semibold text-white" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                      {displayLabel}
                    </h2>
                    <p className="text-xs text-white/80" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                      {format(snapDate, "MMMM d, yyyy")}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Mini graph overlay for image snaps - below title */}
              {displaySnap?.user_id && displaySnap?.metric_name && (
                <div 
                  className="absolute left-4 z-10" 
                  style={{ top: '84px' }}
                >
                  <div className="bg-black/30 backdrop-blur-sm rounded-lg inline-block">
                    <MiniGraphOverlay
                      userId={displaySnap.user_id}
                      metricKey={displaySnap.metric_name}
                      metricEmoji={displayEmoji}
                      metricLabel={displayLabel}
                      snapDate={snapDate}
                      lightText={true}
                    />
                  </div>
                </div>
              )}
              
              {/* Close button - top right */}
              <button
                onClick={isViewingSuggestedSnap ? handleExitDiscover : onClose}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-black/40"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </>
          )}

          {/* Content - scrollable */}
          <div className="flex-1 overflow-y-auto pointer-events-none">
            {(displaySnap?.image_url || displaySnap?.video_url) ? (
              // Layout with image/video
              <div className="relative w-full pointer-events-none">
                {/* Loading indicator */}
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted min-h-[400px]">
                    <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                  </div>
                )}
                
                {/* Image/Video container */}
                <div 
                  className="relative w-full pointer-events-none"
                  style={{
                    height: useHigherPosition 
                      ? 'calc(100vh - env(safe-area-inset-top))'
                      : '100vh'
                  }}
                >
                  {displaySnap?.video_url ? (
                    <video
                      ref={forceAutoPlay}
                      src={displaySnap.video_url}
                      className="w-full h-full object-cover pointer-events-none"
                      style={{ 
                        display: imageLoading ? 'none' : 'block',
                        objectPosition: useHigherPosition ? 'center bottom' : 'center center',
                      }}
                      autoPlay
                      loop
                      muted
                      playsInline
                      onLoadedData={() => {
                        console.log("Video loaded successfully, setting imageLoading to false");
                        if (displaySnap?.id) {
                          imageLoadedRef.set(displaySnap.id, true);
                        }
                        setImageLoading(false);
                      }}
                      onError={(e) => {
                        console.error("Video failed to load:", e, "URL:", displaySnap.video_url);
                        setImageLoading(false);
                      }}
                    />
                  ) : (
                  <img
                    src={displaySnap.image_url}
                    alt={t('accessibility.labels.progress_snap')}
                    className="w-full h-full object-cover pointer-events-none"
                    style={{ 
                      display: imageLoading ? 'none' : 'block',
                      objectPosition: useHigherPosition ? 'center bottom' : 'center center',
                      transition: isFeaturedSnap ? 'filter 0.8s ease-in-out' : undefined,
                      filter: isFeaturedSnap && featuredBlurred ? 'blur(20px) brightness(0.6)' : undefined,
                    }}
                    onLoad={() => {
                      console.log("Image loaded successfully, setting imageLoading to false");
                      if (displaySnap?.id) {
                        imageLoadedRef.set(displaySnap.id, true);
                      }
                      setImageLoading(false);
                      console.log("After setImageLoading(false), cached:", displaySnap?.id);
                      // Featured snap: start 4s countdown only after image fully loads
                      if (isFeaturedSnap && !featuredImageLoadedRef.current) {
                        featuredImageLoadedRef.current = true;
                        featuredTimerRef.current = setTimeout(() => setFeaturedBlurred(true), 4000);
                      }
                    }}
                    onError={(e) => {
                      console.error("Image failed to load:", e, "URL:", displaySnap.image_url);
                      setImageLoading(false);
                    }}
                  />
                  )}
                  
                  {/* Horizontal comparison overlay */}
                  {hasMetricSnaps && !imageLoading && (
                    <div className="absolute inset-0 z-[1] overflow-hidden">
                      {(() => {
                        const hDrag = swipeAxis === 'horizontal' && touchStartX !== null && touchCurrentX !== null
                          ? touchCurrentX - touchStartX : 0;
                        const sw = typeof window !== 'undefined' ? window.innerWidth : 375;
                        const transStyle = swipeAxis === 'horizontal' ? 'none' : 'transform 300ms ease-out';
                        
                        const getTranslateX = (pageOffset: number) => {
                          if (swipeAxis === 'horizontal') return pageOffset * sw + hDrag;
                          if (hTransitionTarget !== null) return (pageOffset - hTransitionTarget) * sw;
                          return pageOffset * sw;
                        };
                        
                        const pagesToRender = [hPageIndex - 1, hPageIndex, hPageIndex + 1]
                          .filter(p => p >= 0 && p <= maxHPage);
                        
                        return pagesToRender.map(pageIdx => (
                          <div key={pageIdx} className="absolute inset-0" style={{
                            transform: `translateX(${getTranslateX(pageIdx - hPageIndex)}px)`,
                            transition: transStyle,
                          }}>
                            {renderHPage(pageIdx)}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  
                  {/* Featured snap promo overlay - appears after 2s with blur */}
                  {isFeaturedSnap && featuredBlurred && !imageLoading && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      style={{
                        animation: 'featuredOverlayFadeIn 0.8s ease-in-out forwards',
                      }}
                    >
                      <img
                        src={FEATURED_SNAP_OVERLAY_URL}
                        alt={t('accessibility.labels.progress_snaps_overlay')}
                        className="pointer-events-none"
                        style={{
                          maxWidth: '85%',
                          maxHeight: '75%',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.5))',
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Gradient fade at top */}
                  {!imageLoading && (
                    <div 
                      className="absolute top-0 left-0 right-0 pointer-events-none"
                      style={{
                        height: '15px',
                        background: 'linear-gradient(to bottom, hsl(var(--background)), transparent)',
                      }}
                    />
                  )}
                  
                  {/* Gradient fade at bottom - only for non-own snaps viewing other users */}
                  {!imageLoading && !isOwnSnap && (
                    <div 
                      className="absolute bottom-0 left-0 right-0 h-2 pointer-events-none"
                      style={{
                        background: 'linear-gradient(to bottom, transparent, hsl(var(--background)))',
                      }}
                    />
                  )}
                  
                  {/* Overlay elements with drop shadow */}
                  {!imageLoading && (
                    <>
                      {/* Note - bottom left (hidden in split/before-after view) */}
                      {displaySnap?.text_content && !isSplitView && (
                        <div 
                          className="absolute left-4 right-24 pointer-events-none"
                          style={{ 
                            bottom: useHigherPosition ? '115px' : '43px',
                            zIndex: 1
                          }}
                        >
                          <div className="relative">
                            <div 
                              className="text-white text-sm whitespace-pre-wrap max-h-[120px] overflow-y-auto pr-2 pointer-events-auto"
                              style={{ 
                                textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.8)',
                                maskImage: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 85%, transparent 100%)',
                                WebkitMaskImage: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 85%, transparent 100%)',
                                paddingBottom: '16px',
                              }}
                              onScroll={handleScroll}
                              onTouchStart={(e) => e.stopPropagation()}
                              onTouchMove={(e) => e.stopPropagation()}
                              onTouchEnd={(e) => e.stopPropagation()}
                            >
                              {displaySnap.text_content}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Right side vertical stack: like button, view count, 3-dot menu, profile pic */}
                      {/* Hidden entirely in split/before-after view */}
                      {!isSplitView && (
                      <div 
                        className="absolute right-4 flex flex-col items-center pointer-events-auto"
                        style={{ 
                          bottom: useHigherPosition ? '115px' : '43px',
                          gap: '24px',
                          zIndex: 10
                        }}
                      >
                        {/* Like button / View count - top */}
                        {currentUserId && isOwnSnap ? (
                          // For own snaps: show view count and like count
                          <>
                            {likeCount > 0 && (
                              <div className="relative flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                                <Heart className="h-6 w-6 text-pink-500 fill-pink-500" />
                                <span className="absolute -bottom-3 text-white text-xs font-semibold" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                                  {likeCount}
                                </span>
                              </div>
                            )}
                            <div className="relative flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                              <Eye className="h-6 w-6 text-white" />
                              <span className="absolute -bottom-3 text-white text-xs font-semibold" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                                {displaySnap?.view_count || 0}
                              </span>
                            </div>
                          </>
                        ) : currentUserId ? (
                          // For other users' snaps: show like button AND view count
                          <>
                            <button
                              onClick={() => likeMutation.mutate()}
                              disabled={likeMutation.isPending}
                              className="relative flex items-center justify-center"
                              style={{ width: '48px', height: '48px' }}
                            >
                              {likeMutation.isPending ? (
                                <MiniGridLoader className="h-6 w-6 text-white" />
                              ) : (
                                <Heart 
                                  className={`h-6 w-6 ${userHasLiked ? 'fill-pink-500 text-pink-500' : 'text-white'}`}
                                />
                              )}
                              {likeCount > 0 && (
                                <span className="absolute -bottom-3 text-white text-xs font-semibold" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                                  {likeCount}
                                </span>
                              )}
                            </button>
                            {/* Show view count for other users' snaps */}
                            <div className="relative flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                              <Eye className="h-6 w-6 text-white" />
                              <span className="absolute -bottom-3 text-white text-xs font-semibold" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                                {displaySnap?.view_count || 0}
                              </span>
                            </div>
                          </>
                        ) : null}

                        {/* View entries button */}
                        {currentUserId && canViewProfileData && (
                          <div className="flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                            <button
                              onClick={() => setDailyEntriesModalOpen(true)}
                              className="flex items-center justify-center w-12 h-12 touch-manipulation active:opacity-70 hover:opacity-80"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              title={t('accessibility.labels.view_detailed_entries')}
                            >
                              <List className="h-6 w-6 text-white" />
                            </button>
                          </div>
                        )}

                        {/* 3-dot menu */}
                        {currentUserId && (
                          <div className="flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button 
                                  className="flex items-center justify-center w-12 h-12 touch-manipulation active:opacity-70"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <MoreVertical className="h-6 w-6 text-white" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="z-[70]">
                                <DropdownMenuItem onClick={handleShare}>
                                  <Share2 className="h-4 w-4 mr-2" />
                                  Share
                                </DropdownMenuItem>
                                {isOwnSnap ? (
                                  <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} className="text-destructive">
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => setShowReportDialog(true)}>
                                    <Flag className="h-4 w-4 mr-2" />
                                    Report
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                        
                        {/* Profile picture - bottom */}
                        {userProfile?.avatar_url && (
                          <div className="flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                            <button
                              onClick={handleProfileClick}
                              className="w-12 h-12 rounded-full overflow-hidden border-2 border-white cursor-pointer hover:opacity-90 transition-opacity touch-manipulation active:opacity-70"
                              style={{ 
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                                WebkitTapHighlightColor: 'transparent'
                              }}
                            >
                              {avatarLoading && (
                                <div className="w-full h-full bg-muted animate-pulse" />
                              )}
                              <img
                                src={userProfile.avatar_url}
                                alt={userProfile.username || 'User'}
                                className="w-full h-full object-cover pointer-events-none"
                                onLoad={() => setAvatarLoading(false)}
                                onError={() => setAvatarLoading(false)}
                                style={{ display: avatarLoading ? 'none' : 'block' }}
                              />
                            </button>
                          </div>
                        )}
                      </div>
                      )}
                      
                      {/* Preview thumbnails + "swipe horizontally" hint - centered view only */}
                      {hasMetricSnaps && isHCenteredView && (hPrevSnap || hNextSnap) && (
                        <div className="absolute left-4 right-24 pointer-events-none flex items-center justify-center gap-3"
                          style={{ 
                            bottom: useHigherPosition ? '240px' : '168px',
                            zIndex: 12,
                          }}>
                          {hPrevSnap ? (
                            <div style={{ width: '36px', height: '48px', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)', opacity: 0.7, flexShrink: 0 }}>
                              {hPrevSnap.image_url ? (
                                <img src={hPrevSnap.image_url} alt="" className="w-full h-full object-cover" />
                              ) : hPrevSnap.video_url ? (
                                <video src={hPrevSnap.video_url} className="w-full h-full object-cover" muted preload="metadata" />
                              ) : null}
                            </div>
                          ) : <div style={{ width: '36px', flexShrink: 0 }} />}
                          
                          <span className="text-white/25 text-[10px] font-medium tracking-wider whitespace-nowrap"
                            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                            swipe horizontally
                          </span>
                          
                          {hNextSnap ? (
                            <div style={{ width: '36px', height: '48px', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)', opacity: 0.7, flexShrink: 0 }}>
                              {hNextSnap.image_url ? (
                                <img src={hNextSnap.image_url} alt="" className="w-full h-full object-cover" />
                              ) : hNextSnap.video_url ? (
                                <video src={hNextSnap.video_url} className="w-full h-full object-cover" muted preload="metadata" />
                              ) : null}
                            </div>
                          ) : <div style={{ width: '36px', flexShrink: 0 }} />}
                        </div>
                      )}
                      
                      {/* Split view navigation arrows - 4 arrows for independent snap switching */}
                      {isSplitView && metricSnaps.length > 2 && (
                        <div className="absolute inset-0 flex" style={{ zIndex: 14 }}>
                          {/* Left side arrows */}
                          <div className="w-1/2 h-full relative">
                            {(effectiveLeftIdx ?? 0) > 0 && (
                              <button
                                className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/70 active:bg-black/60 transition-colors"
                                onClick={(e) => { e.stopPropagation(); setSplitLeftCustomIdx(prev => Math.max((prev ?? effectiveLeftIdx ?? 0) - 1, 0)); }}
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                            )}
                            {(effectiveLeftIdx ?? 0) < metricSnaps.length - 1 && effectiveLeftIdx !== (effectiveRightIdx ?? 0) - 1 && (
                              <button
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/70 active:bg-black/60 transition-colors"
                                onClick={(e) => { e.stopPropagation(); setSplitLeftCustomIdx(prev => Math.min((prev ?? effectiveLeftIdx ?? 0) + 1, metricSnaps.length - 1)); }}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {/* Right side arrows */}
                          <div className="w-1/2 h-full relative">
                            {(effectiveRightIdx ?? 0) > 0 && effectiveRightIdx !== (effectiveLeftIdx ?? 0) + 1 && (
                              <button
                                className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/70 active:bg-black/60 transition-colors"
                                onClick={(e) => { e.stopPropagation(); setSplitRightCustomIdx(prev => Math.max((prev ?? effectiveRightIdx ?? 0) - 1, 0)); }}
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                            )}
                            {(effectiveRightIdx ?? 0) < metricSnaps.length - 1 && (
                              <button
                                className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/70 active:bg-black/60 transition-colors"
                                onClick={(e) => { e.stopPropagation(); setSplitRightCustomIdx(prev => Math.min((prev ?? effectiveRightIdx ?? 0) + 1, metricSnaps.length - 1)); }}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* "X days" label - split comparison view only */}
                      {isSplitView && splitDaysDelta > 0 && (
                        <div className="absolute bottom-8 left-0 right-0 flex justify-center pointer-events-none" style={{ zIndex: 15 }}>
                          <span className="text-white text-lg font-semibold px-4 py-1.5 rounded-full bg-black/30 backdrop-blur-sm"
                            style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
                            {splitDaysDelta} {splitDaysDelta === 1 ? 'day' : 'days'}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Swipe indicator - shows on first snap only at lower third */}
                  {(() => {
                    const hasSameDateSnaps = sameDateSnaps && sameDateSnaps.length > 1;
                    const isFirstSnapInSession = !hasShownSwipeIndicator;
                    // Show indicator if there are multiple snaps to swipe through OR if we can fetch suggested snaps
                    const hasMoreSnaps = hasSameDateSnaps || (!isOwnSnap && !isLoadingSuggested);
                    const shouldShow = currentUserId && isFirstSnapInSession && hasMoreSnaps;
                    
                    console.log('[SwipeIndicator]', {
                      hasSameDateSnaps,
                      sameDateSnapsLength: sameDateSnaps?.length,
                      isFirstSnapInSession,
                      hasMoreSnaps,
                      currentUserId,
                      shouldShow
                    });
                    
                    return shouldShow;
                  })() && (
                    <div 
                      className="absolute inset-0 flex items-end justify-center pointer-events-none pb-32"
                      style={{
                        animation: 'swipeIndicator 3s ease-in-out forwards',
                      }}
                    >
                      <div 
                        className="flex flex-col items-center gap-3"
                        style={{
                          animation: 'bob 2s ease-in-out infinite',
                        }}
                      >
                        <div 
                          className="w-16 h-16 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center"
                          style={{
                            animation: 'flutter 0.6s ease-in-out 3',
                          }}
                        >
                          <ChevronDown className="h-8 w-8 text-white" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }} />
                        </div>
                        <span className="text-sm text-white font-medium" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>{t('ui.text.swipe_to_explore')}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Layout without image - regular background
              <div className="p-6 pt-8 space-y-4">
                {displaySnap?.text_content && (
                  <div className="flex items-start gap-3 pr-20">
                    <div className="flex-1 relative">
                      <div 
                        id="snap-text-content"
                        className="p-4 bg-muted/50 rounded-lg max-h-[40vh] overflow-y-auto relative"
                        onScroll={handleScroll}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                        onTouchEnd={(e) => e.stopPropagation()}
                      >
                        <p className="text-sm whitespace-pre-wrap">{displaySnap.text_content}</p>
                      </div>
                      {/* Fade effect at bottom - only show if content is scrollable */}
                      {isTextScrollable && (
                        <div 
                          className="absolute bottom-0 left-0 right-0 rounded-b-lg pointer-events-none"
                          style={{
                            height: '48px',
                            background: isScrolledToBottom 
                              ? 'linear-gradient(to bottom, transparent 0%, hsl(var(--muted) / 0.3) 80%, hsl(var(--muted) / 0.5) 100%)'
                              : 'linear-gradient(to bottom, transparent 0%, hsl(var(--muted) / 0.4) 60%, hsl(var(--muted) / 0.5) 100%)',
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
                
                {/* Right side vertical stack for text-only snaps */}
                <div 
                  className="fixed right-6 flex flex-col items-center pointer-events-auto"
                  style={{ 
                    bottom: useHigherPosition ? '120px' : '48px',
                    gap: '24px',
                    zIndex: 10
                  }}
                >
                  {/* Like button / View count */}
                  {currentUserId && isOwnSnap ? (
                    // For own snaps: show view count and like count
                    <>
                      {likeCount > 0 && (
                        <div className="relative flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                          <Heart className="h-6 w-6 text-pink-500 fill-pink-500" />
                          <span className="absolute -bottom-3 text-xs font-semibold text-muted-foreground">
                            {likeCount}
                          </span>
                        </div>
                      )}
                      <div className="relative flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                        <Eye className="h-6 w-6" />
                        <span className="absolute -bottom-3 text-xs font-semibold text-muted-foreground">
                          {displaySnap?.view_count || 0}
                        </span>
                      </div>
                    </>
                  ) : currentUserId ? (
                    // For other users' snaps: show like button AND view count
                    <>
                      <button
                        onClick={() => likeMutation.mutate()}
                        disabled={likeMutation.isPending}
                        className="relative flex items-center justify-center"
                        style={{ width: '48px', height: '48px' }}
                      >
                        <Heart 
                          className={`h-6 w-6 ${userHasLiked ? 'fill-pink-500 text-pink-500' : ''}`}
                        />
                        {likeCount > 0 && (
                          <span className="absolute -bottom-3 text-xs font-semibold text-muted-foreground">
                            {likeCount}
                          </span>
                        )}
                      </button>
                      {/* Show view count for other users' snaps */}
                      <div className="relative flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                        <Eye className="h-6 w-6" />
                        <span className="absolute -bottom-3 text-xs font-semibold text-muted-foreground">
                          {displaySnap?.view_count || 0}
                        </span>
                      </div>
                    </>
                  ) : null}

                  {/* View entries button */}
                  {currentUserId && canViewProfileData && (
                    <div className="flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                      <button
                        onClick={() => setDailyEntriesModalOpen(true)}
                        className="flex items-center justify-center w-12 h-12 touch-manipulation active:opacity-70 hover:opacity-80"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        title={t('accessibility.labels.view_detailed_entries')}
                      >
                        <List className="h-6 w-6" />
                      </button>
                    </div>
                  )}

                  {/* 3-dot menu */}
                  {currentUserId && (
                    <div className="flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button 
                            className="flex items-center justify-center w-12 h-12 touch-manipulation active:opacity-70"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <MoreVertical className="h-6 w-6" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-[70]">
                          <DropdownMenuItem onClick={handleShare}>
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                          {isOwnSnap ? (
                            <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setShowReportDialog(true)}>
                              <Flag className="h-4 w-4 mr-2" />
                              Report
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  
                  {/* Profile picture - bottom */}
                  {userProfile?.avatar_url && (
                    <div className="flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
                      <button
                        onClick={handleProfileClick}
                        className="w-12 h-12 rounded-full overflow-hidden border-2 border-border cursor-pointer hover:opacity-90 transition-opacity touch-manipulation active:opacity-70"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {avatarLoading && (
                          <div className="w-full h-full bg-muted animate-pulse" />
                        )}
                        <img
                          src={userProfile.avatar_url}
                          alt={userProfile.username || 'User'}
                          className="w-full h-full object-cover pointer-events-none"
                          onLoad={() => setAvatarLoading(false)}
                          onError={() => setAvatarLoading(false)}
                          style={{ display: avatarLoading ? 'none' : 'block' }}
                        />
                      </button>
                    </div>
                  )}
                  
                </div>
              </div>
            )}

            {/* Swipe indicator for text-only snaps - positioned relative to the full snap container */}
            {!displaySnap?.image_url && !displaySnap?.video_url && (() => {
              const hasSameDateSnaps = sameDateSnaps && sameDateSnaps.length > 1;
              const isFirstSnapInSession = !hasShownSwipeIndicator;
              // Show indicator if there are multiple snaps to swipe through OR if we can fetch suggested snaps
              const hasMoreSnaps = hasSameDateSnaps || (!isOwnSnap && !isLoadingSuggested);
              const shouldShow = currentUserId && isFirstSnapInSession && hasMoreSnaps;
              
              console.log('[SwipeIndicator-TextOnly]', {
                hasSameDateSnaps,
                sameDateSnapsLength: sameDateSnaps?.length,
                isFirstSnapInSession,
                hasMoreSnaps,
                currentUserId,
                shouldShow
              });
              
              return shouldShow;
            })() && (
              <div 
                className="absolute inset-0 flex items-end justify-center pointer-events-none pb-32"
                style={{
                  animation: 'swipeIndicator 3s ease-in-out forwards',
                }}
              >
                <div 
                  className="flex flex-col items-center gap-3"
                  style={{
                    animation: 'bob 2s ease-in-out infinite',
                  }}
                >
                  <div 
                    className="w-16 h-16 rounded-full bg-muted/30 backdrop-blur-sm flex items-center justify-center"
                    style={{
                      animation: 'flutter 0.6s ease-in-out 3',
                    }}
                  >
                    <ChevronDown className="h-8 w-8" />
                  </div>
                  <span className="text-sm text-muted-foreground font-medium">{t('ui.text.swipe_to_explore')}</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Bottom fade effect - only when no navbar */}
          {!useHigherPosition && (
            <div 
              className="absolute bottom-0 left-0 right-0 pointer-events-none"
              style={{
                height: '5px',
                background: 'linear-gradient(to bottom, transparent, hsl(var(--background)))',
              }}
            />
          )}
          </div>

          {/* CSS animations for swipe indicator */}
          <style>{`
            @keyframes flutter {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }
            @keyframes bob {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-8px); }
            }
            @keyframes swipeIndicator {
              0% { opacity: 0; }
              10% { opacity: 1; }
              80% { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>

          {/* CSS animations for swipe indicator */}
          <style>{`
            @keyframes flutter {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }
            @keyframes bob {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-8px); }
            }
            @keyframes swipeIndicator {
              0% { opacity: 0; }
              10% { opacity: 1; }
              80% { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>

          {/* Previous snap preview - positioned above current snap */}
          {(prevSnapPreview || (isTransitioning && prevSnapPreview)) && (
            <div 
              className="absolute inset-0 bg-background overflow-hidden flex flex-col"
              style={{ 
                transform: isTransitioning
                  ? 'translateY(0)'
                  : isDragging && touchStartY !== null && touchCurrentY !== null
                    ? `translateY(calc(-100% + ${Math.abs(Math.min(0, touchStartY - touchCurrentY))}px))`
                    : 'translateY(-100%)',
                transition: isDragging ? 'none' : 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1)',
                borderTopLeftRadius: isDesktop ? '1rem' : (useHigherPosition ? '1rem' : '0'),
                borderTopRightRadius: isDesktop ? '1rem' : (useHigherPosition ? '1rem' : '0'),
                borderBottomLeftRadius: isDesktop ? '1rem' : '0',
                borderBottomRightRadius: isDesktop ? '1rem' : '0',
              }}
            >
              {/* Preview of previous snap */}
              {(() => {
                const previewSnap = prevSnapPreview;
                return (previewSnap?.image_url || previewSnap?.video_url) ? (
                  <div className="relative w-full h-full">
                    {previewSnap.video_url ? (
                      <video
                        ref={forceAutoPlay}
                        src={previewSnap.video_url}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: useHigherPosition ? 'center bottom' : 'center center' }}
                        autoPlay loop muted playsInline
                      />
                    ) : (
                      <img
                        src={previewSnap.image_url}
                        alt={t('accessibility.labels.previous_snap')}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: useHigherPosition ? 'center bottom' : 'center center' }}
                        loading="eager"
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Next snap preview - positioned below current snap for reels effect */}
          {(nextSnapPreview || (isTransitioning && !prevSnapPreview)) && (
            <div 
              className="absolute inset-0 bg-background overflow-hidden flex flex-col"
              style={{ 
                transform: isTransitioning
                  ? 'translateY(0)'
                  : isDragging && touchStartY !== null && touchCurrentY !== null
                    ? `translateY(calc(100% - ${Math.max(0, touchStartY - touchCurrentY)}px))`
                    : 'translateY(100%)',
                transition: isDragging ? 'none' : 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1)',
                borderTopLeftRadius: isDesktop ? '1rem' : (useHigherPosition ? '1rem' : '0'),
                borderTopRightRadius: isDesktop ? '1rem' : (useHigherPosition ? '1rem' : '0'),
                borderBottomLeftRadius: isDesktop ? '1rem' : '0',
                borderBottomRightRadius: isDesktop ? '1rem' : '0',
              }}
            >
              {/* Preview of next snap - simple media display */}
              {(() => {
                const previewSnap = nextSnapPreview || (isTransitioning && currentSuggestedIndex < suggestedSnaps.length - 1 ? suggestedSnaps[currentSuggestedIndex + 1] : null);
                return (previewSnap?.image_url || previewSnap?.video_url) ? (
                  <div className="relative w-full h-full">
                    {previewSnap.video_url ? (
                      <video
                        ref={forceAutoPlay}
                        src={previewSnap.video_url}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: useHigherPosition ? 'center bottom' : 'center center' }}
                        autoPlay loop muted playsInline
                      />
                    ) : (
                      <img
                        src={previewSnap.image_url}
                        alt={t('accessibility.labels.next_snap')}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: useHigherPosition ? 'center bottom' : 'center center' }}
                        loading="eager"
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Report Dialog */}
      <AlertDialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <AlertDialogContent className="z-[80]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ui.text.report_progress_snap')}</AlertDialogTitle>
            <AlertDialogDescription>
              This will report the progress snap for review. Our team will investigate
              and take appropriate action if the content violates our guidelines.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReport} disabled={isReporting}>
              {isReporting ? t('report.reporting') : t('report.submit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="z-[80]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ui.text.delete_progress_snap')}</AlertDialogTitle>
            <AlertDialogDescription>{t('ui.text.this_will_permanently_delete_your_progress_snap_th')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isDeleting ? t('dashboard.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Daily Entries Modal */}
      {currentUserId && canViewProfileData && snap && (
        <DailyEntriesModal
          open={dailyEntriesModalOpen}
          onOpenChange={setDailyEntriesModalOpen}
          userId={snap.user_id}
          currentUserId={currentUserId}
          entryDate={snap.snap_date}
          metricKey={snap.metric_name}
          metricName={displayLabel}
          metricEmoji={displayEmoji}
        />
      )}
    </>
  );
};
